import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import CopyIconButton from '../../../../shared/components/CopyIconButton';
import PreviewRailShell from '../PreviewRailShell';
import { usePreviewEventDataset } from '../../hooks/useEventDatasets';
import { plainPreviewError } from '../../utils/datasetFormUtils';
import { inputLabel } from '../../utils/datasetMappingUtils';
import type { EventDataset, EventDatasetPreviewRow, EventDatasetPreviewRows } from '../../../../types/backend';

interface DatasetPreviewProps {
  customerId: string;
  /** The current draft, built when the operator asks for a preview. */
  buildDraft: () => EventDataset;
  /** The sample event in the editor; previewed beside the recent events when it holds one. */
  sample: Record<string, unknown>;
  /** The form problems that stop a preview, as saving would name them; empty sends the draft. */
  check: () => string[];
}

interface MisfitTally {
  /** The input the rows were read with; named only when the draft has several. */
  input: number | null;
  column: string;
  /** A misfit's reason, or the value a translation did not cover. */
  what: string;
  rows: number;
}

// Misfits are a row's problems; values kept without a translation are its notes, tallied apart: those rows fit.
function tallyMisfits(part: EventDatasetPreviewRows, several: boolean, of: 'problems' | 'notes' = 'problems'): MisfitTally[] {
  const tallies = new Map<string, MisfitTally>();
  for (const row of part.rows) {
    const input = several ? row.input : null;
    const found = of === 'problems'
      ? row.problems.map((p) => ({ column: p.column, what: p.reason }))
      : row.notes.map((n) => ({ column: n.column, what: n.value }));
    for (const f of found) {
      const key = `${input}\u0000${f.column}\u0000${f.what}`;
      const tally = tallies.get(key) ?? { input, column: f.column, what: f.what, rows: 0 };
      tally.rows += 1;
      tallies.set(key, tally);
    }
  }
  return [...tallies.values()];
}

function columnsOf(part: EventDatasetPreviewRows): string[] {
  const names: string[] = [];
  for (const row of part.rows) {
    for (const name of Object.keys(row.values)) {
      if (!names.includes(name)) names.push(name);
    }
  }
  return names;
}

// The rail is narrow: customer is the same on every row and is left out, and the envelope columns share one line.
const LEFT_OUT = ['customer'];
const WHERE_COLUMNS = ['account', 'region', 'event_time'];

interface RowShape {
  /** The key fields, shown first on each card. */
  keyFields: string[];
  /** The operator's own columns, shown as label and value under the key. */
  columns: string[];
  /** The draft has more than one input, so each row names the one it was read with. */
  severalInputs: boolean;
}

const keptText = (row: EventDatasetPreviewRow) =>
  `Kept: ${row.notes.map((n) => `${n.column} ${n.value}`).join('; ')}`;

const fitText = (row: EventDatasetPreviewRow) =>
  row.problems.length === 0 ? 'Fits' : `Does not fit: ${row.problems.map((p) => `${p.column} ${p.reason}`).join('; ')}`;

// Long values such as ARNs share their start, so the card keeps the start, cut after a ':' or '/' where one is
// near, and the distinguishing tail.
const CARD_VALUE_MAX = 44;
const CARD_VALUE_HEAD = 24;

function shortenMiddle(value: string): string {
  if (value.length <= CARD_VALUE_MAX) return value;
  const boundary = Math.max(value.lastIndexOf(':', CARD_VALUE_HEAD - 1), value.lastIndexOf('/', CARD_VALUE_HEAD - 1));
  const head = boundary >= CARD_VALUE_HEAD / 2 ? boundary + 1 : CARD_VALUE_HEAD;
  return `${value.slice(0, head)}…${value.slice(value.length - (CARD_VALUE_MAX - head - 1))}`;
}

/** One row in full: every column wrapped, each with a copy button, and whether it fits. */
const RowDialog: React.FC<{ row: EventDatasetPreviewRow; open: boolean; onClose: () => void }> = ({ row, open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Object.entries(row.values).map(([column, value]) => (
        <Box key={column} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>{column}</Typography>
          <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
            {value ?? ''}
          </Typography>
          <CopyIconButton text={value ?? ''} tooltip={`Copy ${column}`} />
        </Box>
      ))}
      <Typography variant="body2" color={row.problems.length === 0 ? 'success.main' : 'error.main'}>{fitText(row)}</Typography>
      {row.notes.length > 0 && <Typography variant="body2" color="text.secondary">{keptText(row)}</Typography>}
    </DialogContent>
    <DialogActions sx={{ px: 2, pb: 2 }}>
      <Button size="small" variant="contained" onClick={onClose}>Done</Button>
    </DialogActions>
  </Dialog>
);

const RowCard: React.FC<{ row: EventDatasetPreviewRow; shape: RowShape }> = ({ row, shape }) => {
  const [open, setOpen] = useState(false);
  const keyValues = shape.keyFields.map((k) => row.values[k] ?? '').filter(Boolean);
  const where = WHERE_COLUMNS.map((c) => row.values[c]).filter(Boolean).join(' · ');
  const fits = row.problems.length === 0;
  return (
    <>
      <ButtonBase
        component="div"
        data-testid="dataset-preview-row"
        onClick={() => setOpen(true)}
        aria-label={`Show the full row ${keyValues.join(' · ')}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          textAlign: 'left',
          gap: 0.25,
          p: 1,
          minWidth: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          title={keyValues.join(' · ')}
          data-testid="dataset-preview-row-key"
          sx={{ fontFamily: 'monospace' }}
        >
          {keyValues.length === 0 ? 'No key value' : keyValues.map((v, i) => (
            <React.Fragment key={i}>{i > 0 && ' · '}<span>{shortenMiddle(v)}</span></React.Fragment>
          ))}
        </Typography>
        {shape.columns.filter((c) => !shape.keyFields.includes(c)).map((c) => (
          <Box key={c} sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{c}:</Typography>
            <Typography variant="caption" noWrap title={row.values[c] ?? ''} sx={{ fontFamily: 'monospace' }}>
              {shortenMiddle(row.values[c] ?? '')}
            </Typography>
          </Box>
        ))}
        {where && <Typography variant="caption" color="text.secondary" noWrap title={where}>{where}</Typography>}
        {shape.severalInputs && <Typography variant="caption" color="text.secondary">{inputLabel(row.input)}</Typography>}
        <Typography variant="caption" color={fits ? 'success.main' : 'error.main'}>{fitText(row)}</Typography>
        {row.notes.length > 0 && <Typography variant="caption" color="text.secondary">{keptText(row)}</Typography>}
      </ButtonBase>
      <RowDialog row={row} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const MisfitList: React.FC<{ part: EventDatasetPreviewRows; severalInputs: boolean; testId?: string }> = ({ part, severalInputs, testId }) => {
  const misfits = tallyMisfits(part, severalInputs);
  if (misfits.length === 0) return null;
  return (
    <Box data-testid={testId}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Rows that would not fit</Typography>
      {misfits.map((m) => (
        <Typography key={`${m.input}:${m.column}:${m.what}`} variant="body2" sx={{ pl: 2 }}>
          {m.input !== null && `${inputLabel(m.input)}, `}{m.column}: {m.what} ({m.rows} {m.rows === 1 ? 'row' : 'rows'})
        </Typography>
      ))}
    </Box>
  );
};

/** The values rows keep that no translation covers, by input, column and value; never misfits. */
const KeptList: React.FC<{ part: EventDatasetPreviewRows; severalInputs: boolean; testId?: string }> = ({ part, severalInputs, testId }) => {
  const kept = tallyMisfits(part, severalInputs, 'notes');
  if (kept.length === 0) return null;
  return (
    <Box data-testid={testId}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Values kept without a translation</Typography>
      {kept.map((k) => (
        <Typography key={`${k.input}:${k.column}:${k.what}`} variant="body2" sx={{ pl: 2 }}>
          {k.input !== null && `${inputLabel(k.input)}, `}{k.column} {k.what} ({k.rows} {k.rows === 1 ? 'row' : 'rows'})
        </Typography>
      ))}
    </Box>
  );
};

interface PreviewPartProps {
  title: string;
  part: EventDatasetPreviewRows;
  testId: string;
  matched: string;
  /** The exact window or match the summary stands for, shown on hover. */
  matchedDetail?: string;
  shape: RowShape;
}

/** One part of the answer in the rail: the summary, a card per row, and the rows that would not fit. */
const PreviewPart: React.FC<PreviewPartProps> = ({ title, part, testId, matched, matchedDetail, shape }) => (
  <Box data-testid={testId} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography variant="subtitle2">{title}</Typography>
    <Typography variant="body2" data-testid={`${testId}-matched`} title={matchedDetail}>{matched}</Typography>
    {part.rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">No rows.</Typography>
    ) : (
      part.rows.map((row, i) => <RowCard key={i} row={row} shape={shape} />)
    )}
    <MisfitList part={part} severalInputs={shape.severalInputs} testId={`${testId}-misfits`} />
    <KeptList part={part} severalInputs={shape.severalInputs} testId={`${testId}-kept`} />
  </Box>
);

/** One part of the answer in the expanded view: every column as a table. */
const PreviewTable: React.FC<{ title: string; part: EventDatasetPreviewRows; matched: string; severalInputs: boolean }> = ({
  title, part, matched, severalInputs,
}) => {
  const columns = columnsOf(part);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2">{matched}</Typography>
      {part.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No rows.</Typography>
      ) : (
        <Box sx={{ overflow: 'auto', maxHeight: '50vh' }}>
          <Table size="small" stickyHeader aria-label={`${title} rows`}>
            <TableHead>
              <TableRow>
                {severalInputs && <TableCell>Input</TableCell>}
                {columns.map((c) => <TableCell key={c} sx={{ fontFamily: 'monospace' }}>{c}</TableCell>)}
                <TableCell>Fits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {part.rows.map((row, i) => (
                <TableRow key={i}>
                  {severalInputs && <TableCell sx={{ whiteSpace: 'nowrap' }}>{inputLabel(row.input)}</TableCell>}
                  {columns.map((c) => (
                    <TableCell key={c} sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{row.values[c] ?? ''}</TableCell>
                  ))}
                  <TableCell>{row.problems.length === 0 ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <MisfitList part={part} severalInputs={severalInputs} />
      <KeptList part={part} severalInputs={severalInputs} />
    </Box>
  );
};

const recentSummary = (events: number) => `${events.toLocaleString('en-US')} events in the last 24 hours`;
const sampleSummary = (part: EventDatasetPreviewRows) =>
  (part.events_read > 0 ? 'The sample matches the pattern' : 'The sample does not match the pattern');

const DatasetPreview: React.FC<DatasetPreviewProps> = ({ customerId, buildDraft, sample, check }) => {
  const { preview, result: answer, previewing, error: refusal } = usePreviewEventDataset(customerId);
  const hasSample = Object.keys(sample).length > 0;
  // An answer belongs to the draft and sample it was asked for; once either changes it is no longer shown.
  const draftKey = JSON.stringify([buildDraft(), sample]);
  const [askedKey, setAskedKey] = useState<string | null>(null);
  const current = askedKey === draftKey;
  const result = current ? answer : undefined;
  // A form problem, like a refusal, belongs to the draft it was found in.
  const [problem, setProblem] = useState<{ key: string; text: string } | null>(null);
  const shownProblem = problem?.key === draftKey ? problem.text : null;
  const error = shownProblem ?? (current && refusal ? plainPreviewError(refusal) : null);
  const [expanded, setExpanded] = useState(false);
  const draft = buildDraft();
  const shape: RowShape = {
    keyFields: draft.key_fields,
    columns: draft.columns.map((c) => c.name).filter((c) => !LEFT_OUT.includes(c)),
    severalInputs: draft.inputs.length > 1,
  };

  const ask = () => {
    const problems = check();
    if (problems.length > 0) {
      setProblem({ key: draftKey, text: `Fix before previewing: ${problems.join('; ')}` });
      return;
    }
    setProblem(null);
    setAskedKey(draftKey);
    preview(buildDraft(), hasSample ? sample : undefined);
  };

  return (
    <PreviewRailShell
      testId="dataset-preview"
      runLabel="Preview"
      running={previewing}
      runDisabled={false}
      onRun={ask}
      hasSample={hasSample}
      noSampleMessage="Run to see the rows from the last 24 hours. Add a sample event in step ① to see its rows too."
      idleMessage="Run to see the rows from the last 24 hours and from the sample event."
      error={error}
      onClearError={shownProblem ? () => setProblem(null) : undefined}
      hasResult={!!result}
      headerActions={result && (
        <Button size="small" startIcon={<FullscreenIcon />} onClick={() => setExpanded(true)}>Expand</Button>
      )}
      intro={(
        <Typography variant="caption" color="text.secondary">
          Shows the rows this draft would give from the last 24 hours of events and from the sample event. Nothing
          is kept: the preview saves no rows and no definition.
        </Typography>
      )}
    >
      {result && (
        <>
          <PreviewPart
            title="Last 24 hours"
            testId="dataset-preview-recent"
            part={result.recent}
            matched={recentSummary(result.recent.events_read)}
            matchedDetail={`${result.since} to ${result.until}`}
            shape={shape}
          />
          {result.sample && (
            <PreviewPart
              title="Sample event"
              testId="dataset-preview-sample"
              part={result.sample}
              matched={sampleSummary(result.sample)}
              shape={shape}
            />
          )}
          <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth="lg" fullWidth>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <PreviewTable
                title="Last 24 hours"
                part={result.recent}
                matched={`${recentSummary(result.recent.events_read)}, from ${result.since} to ${result.until}`}
                severalInputs={shape.severalInputs}
              />
              {result.sample && (
                <PreviewTable title="Sample event" part={result.sample} matched={sampleSummary(result.sample)} severalInputs={shape.severalInputs} />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button size="small" variant="contained" onClick={() => setExpanded(false)}>Done</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </PreviewRailShell>
  );
};

export default DatasetPreview;
